// src/components/quotes/QuoteBuilderPage.jsx
//
// The quote builder: a FULL PAGE, controls on the left, the real document on
// the right. It was a modal at `maxWidth: 980` inside MspOsApp.jsx, which left
// no room to show what the client will actually receive.
//
// Shipped in two commits on purpose — the move out of MspOsApp.jsx landed
// verbatim and still a modal, so "did the move break anything" was answered
// before "does the new design work".
import React from "react";
import ClientTypeahead from "../common/ClientTypeahead";
import { quoteCompanyPatch } from "../common/companyLink";
import { formatQuoteNumber } from "./quoteRecord";
import QuoteDocumentPreview from "./QuoteDocumentPreview";
import QuoteAcceptedPanel from "./QuoteAcceptedPanel";
import { quoteCoverPatch, coverImageOf, coverEnabled, coverImageBytes } from "./quoteCover";
// The proposals' image reader — downscales to 1600px before anything reaches
// state. Imported rather than re-written: a second reader would be a second
// place the downscale could be forgotten.
import { readCoverImage, MoneyInput } from "../proposals/proposalChrome";
/* Same one definition the proposal and Stage D use — the panel a person reads
   must not price a line differently from the PDF they send. */
import { quantityOf } from "../proposals/foundationalBlocks";
/* The period rule and the builder's "/mo" shorthand. The document prints the
   words; this panel prints the shorthand; both come from the one module. */
import { periodOf, periodShort, periodHeading, streamsText } from "./quotePeriod";
/* Freight is always the last section, below the common items too. The document
   (backend quoteRenderer.js) carries a PORT of these two functions and a test
   pins the two together; see quoteSectionOrder.js. */
import { isFreightSection, orderSections } from "./quoteSectionOrder";
// A module import, not a prop — it is already one in MspOsApp and passing it
// through would be a prop that exists only to relay an import.
import { isOpen } from "../opportunities/opportunityPipeline";

/**
 * The quote builder.
 *
 * ⚠ THE RIGHT PANE IS A VIEWER, NOT AN EDITABLE DOCUMENT, and that is the
 * design rather than a shortcut. The proposal preview is editable in place
 * because the SPA owns those templates; the quote document is owned by
 * `quoteRenderer.js` on the SERVER, deliberately, because that is what stops
 * the PDF's totals drifting from this panel's. So everything edits HERE, on
 * the left — including the cover image and the title. See QuoteDocumentPreview.
 *
 * ⚠ THE PROP LIST IS LONG ON PURPOSE. Forty callbacks and pieces of state,
 * every one of them a closure that lives in MspOsApp. Collapsing them into a
 * single `ctx` object would hide exactly how much this component still reaches
 * back into that file, and the point of the move is to make that surface
 * visible so it can be reduced deliberately later. Nothing here is passed that
 * the markup does not already use — the list was derived from the compiler,
 * not guessed.
 */
/* ══════════════════════════════════════════════════════════════════════
   BUTTONS THAT SIT ON THE DARK HEADER

   The header ground is var(--strat) #0b80a5. The app's button classes were
   all designed for a light surface — .btn is white on white, .btn-warn and
   .btn-danger are pale tints meant to read as gentle against var(--s2) — so
   on teal they either vanish or shout. These are inline replacements that
   keep the SEMANTIC RANKING (destructive, neutral, primary) but express it in
   tints that work on this ground.

   ⚠ AND ONE OF THOSE CLASSES DOES NOT EXIST. `.btn-strat` — which the Push to
   Autotask button carried in the old footer — has NO rule anywhere in the app.
   The palette defines .btn-S / .btn-O / .btn-C / .btn-fellow / .btn-danger /
   .btn-warn, and `btn-strat` is used in eight places across four files, every
   one of them rendering as a plain white .btn. Worth knowing before anyone
   "restores" it here expecting teal, and worth fixing at the source one day —
   but that is eight buttons in four files and not this change.

   ⚠ THEY DARKEN THE GROUND RATHER THAN LIGHTENING IT, and that is measured,
   not a preference. #0b80a5 is a mid-tone: a translucent WHITE fill lifts the
   surface toward the white label and costs contrast. Composited and scored
   against WCAG 2.1:

       white 0.15 over the header  -> surface #3093b3, label 3.53:1   (fails)
       black 0.18 over the header  -> surface #096987, label 6.21:1
       black 0.30 over the header  -> surface #085a73, label 7.72:1

   3.53 is below the 4.5 floor for 11px text — and it is what the existing
   'Back to quotes' button has always used, so this also lifts a control that
   was already shipped.

   ⚠ .btn:disabled is `opacity: 0.45`, which is a separate property and is NOT
   overridden by an inline background. The disabled states still read as
   disabled; that was worth checking before styling over the class.
   ══════════════════════════════════════════════════════════════════════ */
const HDR_BTN = {
  /* Neutral: back, save, generate. */
  ghost: { fontSize: 11, background: "rgba(0,0,0,0.18)", border: "1px solid rgba(255,255,255,0.38)",
           color: "white", flexShrink: 0 },
  /* One step up: an action, but not THE action. */
  strong: { fontSize: 11, background: "rgba(0,0,0,0.30)", border: "1px solid rgba(255,255,255,0.55)",
            color: "white", fontWeight: 600, flexShrink: 0 },
  /* The primary. Solid, so it is the only thing on the row that reads as a
     filled button — 8.65:1 for its label and 4.52:1 against the header. */
  primary: { fontSize: 11, background: "#ffffff", border: "1px solid #ffffff",
             color: "#06526b", fontWeight: 700, flexShrink: 0 },
  /* Destructive. A red tint light enough to stay translucent composites to a
     muddy grey-mauve on teal (#68505b at 0.55) and stops reading as danger at
     all, so this one is nearly opaque: #723131, label 8.32:1. */
  danger: { fontSize: 11, background: "rgba(140,29,20,0.80)", border: "1px solid rgba(255,160,150,0.75)",
            color: "#ffebe8", flexShrink: 0 },
};

export default function QuoteBuilderPage({
  quote: q,
  // Data
  atCompanies = [], opportunities = [], proposals = [], quoteTemplates = [],
  defaultMarkupPct = 0,
  // Pure helpers — closures in MspOsApp today
  computeQuoteTotals, ensureMigrated, countAllLineItems, sumOptions,
  isSameLocation, isSectionTemplate, templateItemCount,
  // Quote-level
  updateQuote, deleteQuote, setEditingQuoteId,
  // Structure
  addSection, updateSection, confirmAndDeleteSection, moveSection, reorderSections,
  addOption, updateOption, confirmAndDeleteOption, selectOption,
  addLineItem, updateLineItem, deleteLineItem,
  // Focus + drag
  focusedLocation, setFocusedLocation, dragSectionIndexRef,
  // Freight
  setFreightVisibility, setManualFreight, switchToManualFreight, switchToManagedFreight,
  // Templates + AI
  applyTemplate, saveCurrentAsTemplate, openAiChat,
  // Images
  setLightboxImage,
  // Backend actions
  generateQuotePdf, sendQuoteToClient, pushQuoteToAutotask,
  // Terms & conditions
  addQuoteClause, updateQuoteClause, removeQuoteClause, moveQuoteClause,
  restoreQuoteClause,
}) {
  const totals = computeQuoteTotals(q);
  return (
    /* ⚠ A FULL-WIDTH TAB'S ROOT NEEDS ALL THREE OF THESE. The shared tab-content
       container is a flex item with `flex: 0 1 auto`, so a root div with no width
       shrinks to its content — that is how Proposals once rendered at 387px inside
       a 1662px pane. `minWidth:0` matters because the preview holds a fixed 794px
       document that would otherwise set a flex floor the column cannot go under. */
    <div style={{width:"100%",flex:1,minWidth:0,display:"flex",flexDirection:"column"}}>

      {/* Page header — full width above both columns, so the quote's identity
          and the Q number stay visible whichever pane is being scrolled.

          ⚠ IT IS ALSO THE TOOLBAR. The document actions used to sit in a bar
          under both columns, whose comment said they were kept out of the left
          pane's scroll container so Send and Generate PDF stayed reachable
          without scrolling to the bottom of a long quote. That constraint is
          satisfied BETTER here: this row is flexShrink:0, sits above both
          columns, and never scrolls at all. The footer is gone; the reason it
          existed is not, and this is where it now lives. */}
      <div className="pe-hdr" style={{background:"var(--strat)",borderRadius:10,marginBottom:12,flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <button className="btn" style={HDR_BTN.ghost} onClick={()=>setEditingQuoteId(null)}>← Back to quotes</button>
          {/* ⚠ minWidth IS A FLOOR, NOT 0, AND IT IS WHAT MAKES THE ROW WRAP.
              Measured in a browser rather than reasoned about: with minWidth:0
              the row still "fits" at 1100px — a 1366 laptop with the sidenav —
              by squeezing the title box to 253px, narrower than the text in it.
              It looks correct on Craig's 2000px monitor and is unusable on a
              laptop, which is the failure this component has had before.

              360px holds a normal pipeline title ("P1509 - Office Setup -
              Cottesloe Beach Hotel" measures 297px plus padding at 14px bold).
              Below that the button cluster wraps to a second line instead, and
              the title gets the whole first row — a taller header, which is the
              cheaper loss. */}
          <div style={{flex:1,minWidth:360}}>
            {/* ⚠ THE Q NUMBER SITS BESIDE THE TITLE, NEVER INSIDE IT. A pipeline
                quote's TITLE already carries the source proposal's P number; the Q
                identifies the quote. Merging them reads as tidier and loses which
                number is which. */}
            {q.quoteNumber!=null && (
              <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.06em",color:"rgba(255,255,255,0.75)",fontVariantNumeric:"tabular-nums",marginBottom:3}}>{formatQuoteNumber(q.quoteNumber)}</div>
            )}
            <input type="text" value={q.title||""} onChange={e=>updateQuote(q.id,{title:e.target.value})}
              placeholder="Quote title…"
              style={{width:"100%",background:"rgba(255,255,255,0.12)",border:"1px solid rgba(255,255,255,0.2)",borderRadius:6,padding:"6px 10px",color:"white",fontSize:14,fontWeight:700,outline:"none",fontFamily:"var(--ff-head)"}}/>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.7)",marginTop:4}}>
              Quote ID: {q.id} · Created {new Date(q.createdAt).toLocaleDateString()}
              {(q.autotaskTicketNumber||q.autotaskQuoteId) && <> · <span style={{color:"#9be8b6"}}>Pushed to Autotask {q.autotaskTicketNumber || `#${q.autotaskQuoteId}`}</span></>}
            </div>
          </div>
          {/* ⚠ DELETE IS SEPARATED FROM THE SEND CLUSTER, and the divider is
              doing the job the footer's flex:1 spacer used to. deleteQuote
              confirms before it does anything, so a misclick costs a dialog
              rather than a quote — but it now sits inches from Send, and
              distance was the only thing keeping them apart before. */}
          <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0,flexWrap:"wrap"}}>
            <button className="btn" style={HDR_BTN.danger}
              onClick={()=>deleteQuote(q.id)}>🗑 Delete</button>
            <div style={{width:1,alignSelf:"stretch",background:"rgba(255,255,255,0.28)",margin:"0 3px"}}/>
            <button className="btn" style={HDR_BTN.ghost}
              onClick={()=>saveCurrentAsTemplate(q)} disabled={countAllLineItems(q)===0}>📚 Save as template</button>
            <button className="btn" style={HDR_BTN.ghost}
              onClick={()=>generateQuotePdf(q)} disabled={countAllLineItems(q)===0}>📄 Generate PDF</button>
            <button className="btn" style={HDR_BTN.primary}
              onClick={()=>sendQuoteToClient(q)} disabled={countAllLineItems(q)===0}>✉️ Send to client</button>
            <button className="btn" style={HDR_BTN.strong}
              onClick={()=>pushQuoteToAutotask(q)} disabled={countAllLineItems(q)===0||!q.companyId}>🔁 Push to Autotask</button>
          </div>
        </div>
      </div>

      {/* Two columns: editor left, the real document right. Mirrors the proposal
          builder's split so the two feel like one app. */}
      <div style={{display:"grid",gridTemplateColumns:"minmax(300px, 35%) 1fr",gap:14,alignItems:"start"}}>

        {/* ── LEFT: every control. ────────────────────────────────────────────
            ⚠ ITS OWN SCROLL CONTAINER. The app shell is `overflow:hidden` all the
            way up (.body, .app), so a column taller than the viewport is simply
            unreachable — ~1100px of the proposal settings pane was invisible
            until it got exactly this. */}
        <div style={{maxHeight:"calc(100vh - 190px)",overflowY:"auto",paddingRight:4,minWidth:0}}>

          {/* What the client did with the link — and, once they accept, WHAT
              THEY ACCEPTED as an order list. First in the column, because an
              accepted quote's only remaining question is what to order. Reads
              the acceptance snapshot, never this quote, so editing below
              cannot move it; its one write is status -> won, once. */}
          <QuoteAcceptedPanel quote={q} updateQuote={updateQuote} />

          {/* Company + contact + valid until.
              Was three columns across a 980px modal; the left pane is ~35% of the
              page now, so three abreast would crush the company typeahead — the
              one field here that needs room to show its matches. */}
          <div style={{display:"grid",gridTemplateColumns:"1fr",gap:10,marginBottom:14}}>
            <div>
              <label style={{fontSize:10,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.5px"}}>Company</label>
              {/* ⚠ FREE TEXT WITH AN OPTIONAL AUTOTASK LINK — deliberately neither
                  a dropdown nor pure free text.
  
                  This was a hard <select> over atCompanies, which made a prospect,
                  a referral or any un-onboarded business simply unquotable.
  
                  But do NOT fix that the way Proposals did. Proposals deleted its
                  dropdown and went pure free text, and CLAUDE.md records the result:
                  zero of twelve real proposals carry an Autotask companyId, so the
                  onboarding pipeline cannot resolve a company automatically and has
                  to be told one on every single run — permanently.
  
                  Quotes would fare worse still. q.companyId is read in exactly two
                  places and both are Push to Autotask (the button's disabled= and the
                  handler's guard), so pure free text would ship that button already
                  dead for every quote created after the change.
  
                  So companyName is the truth for display, and companyId is an
                  OPTIONAL link that exists only when Craig picked a real company. */}
              <ClientTypeahead
                value={q.companyName||""}
                companies={atCompanies}
                placeholder="Company name — type a new one if they're not in Autotask"
                inputStyle={{fontSize:11,marginTop:4,marginBottom:0}}
                /* ⚠ LINKED ONLY ON AN EXPLICIT PICK, and CLEARED the moment the name
                   is typed over — see quoteCompany.js. A companyId left behind after
                   the name changed points at a different client than the one the
                   quote names: the cross-client mis-file class, and here it would
                   push the quote into the wrong company's Autotask record. The rule
                   lives in a tested module rather than inline precisely because this
                   file has no tests and gets re-exported wholesale. */
                onChange={(name,match)=>updateQuote(q.id,quoteCompanyPatch(name,match))}
              />
              {/* Explains the disabled Push button rather than leaving it mysterious.
                  Never a gate: quoting an unknown company is the point of this field. */}
              {q.companyName ? (
                <div style={{fontSize:9.5,marginTop:3,lineHeight:1.3,
                  color:q.companyId?"var(--dt-secondary)":"var(--text3)"}}>
                  {q.companyId
                    ? "✓ Linked to Autotask"
                    : "Not linked — Push to Autotask unavailable"}
                </div>
              ) : null}
            </div>
            <div>
              <label style={{fontSize:10,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.5px"}}>Contact email</label>
              <input type="email" className="inp" style={{width:"100%",fontSize:11,marginTop:4,marginBottom:0}}
                value={q.contactEmail||""} onChange={e=>updateQuote(q.id,{contactEmail:e.target.value})}
                placeholder="client@company.com"/>
            </div>
            <div>
              <label style={{fontSize:10,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.5px"}}>Valid until</label>
              <input type="date" className="inp" style={{width:"100%",fontSize:11,marginTop:4,marginBottom:0}}
                value={q.validUntil||""} onChange={e=>updateQuote(q.id,{validUntil:e.target.value})}/>
            </div>
          </div>
  
          {/* ── COVER PAGE — optional, off by default ─────────────────────────
              ⚠ NOT every quote wants one. A two-line "supply and install three
              switches" quote should not be made to carry a photographic cover,
              so this is opt-in per quote and an absent flag means off — which is
              also what keeps every quote written before covers existed
              rendering exactly as it did, with no migration.

              ⚠ THE TITLE AND COMPANY ARE NOT REPEATED HERE. The cover prints
              the quote's own title (the field in the page header above) and its
              company (the field in the row above). Giving the cover its own
              copies would be two inputs for one value and they would drift —
              the brief asked for all three in this pane, and two of the three
              were already in it. */}
          {/* Section-level card: the shared --form-card fill (see MspOsApp :root). */}
          <div style={{marginBottom:14,border:"1px solid var(--border2)",borderRadius:8,background:"var(--form-card)",padding:"10px 12px"}}>
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",margin:0}}>
              <input type="checkbox" checked={coverEnabled(q)} style={{margin:0}}
                onChange={e=>updateQuote(q.id,{coverEnabled:e.target.checked})}/>
              <span style={{fontSize:10,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.5px"}}>Cover page</span>
            </label>

            {coverEnabled(q) && (
              <div style={{marginTop:10}}>
                <div style={{fontSize:10,color:"var(--text3)",lineHeight:1.4,marginBottom:8}}>
                  Prints the quote title and company on their own first page. The image is optional.
                </div>
                {coverImageOf(q) ? (
                  <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                    <img src={coverImageOf(q)} alt="Cover"
                      style={{width:96,height:64,objectFit:"cover",borderRadius:4,border:"1px solid var(--border2)",background:"white",cursor:"pointer"}}
                      onClick={()=>setLightboxImage(coverImageOf(q))} title="Click to enlarge"/>
                    <div style={{minWidth:0}}>
                      {/* ⚠ THE WEIGHT IS SHOWN because the ceiling it counts toward
                          fails SILENTLY — an oversized state blob is rejected with
                          nothing but a console.warn, so being able to see this is
                          the only defence a person has. */}
                      <div style={{fontSize:10,color:"var(--text3)",marginBottom:6}}>
                        ~{Math.round(coverImageBytes(q)/1024)} KB in the saved state
                      </div>
                      <button className="btn" style={{fontSize:10,padding:"3px 10px"}}
                        onClick={()=>updateQuote(q.id,quoteCoverPatch(q,null))}>Remove image</button>
                    </div>
                  </div>
                ) : (
                  <label className="btn" style={{fontSize:10,padding:"4px 12px",display:"inline-block",cursor:"pointer"}}>
                    🖼 Choose image
                    {/* Reuses the proposals' reader, which downscales to 1600px
                        before it ever reaches state — an untouched phone photo
                        is several MB of base64 in a shared blob. */}
                    <input type="file" accept="image/*" style={{display:"none"}}
                      onChange={e=>readCoverImage(e,(dataUrl)=>updateQuote(q.id,quoteCoverPatch(q,dataUrl)))}/>
                  </label>
                )}
              </div>
            )}
          </div>

          {/* Opportunity — links this quote into the pipeline board.
              Lists open opportunities, plus the linked one if it has
              since been closed: without that entry the select would
              match no option, render as "— None —", and the next
              change would silently unlink the deal. */}
          {opportunities.length>0&&(()=>{
            const openOpps = opportunities.filter(isOpen);
            const linked = opportunities.find(o=>o.id===q.opportunityId);
            const opts = linked&&!openOpps.some(o=>o.id===linked.id)
              ? [...openOpps,{...linked,closed:true}] : openOpps;
            return (
              <div style={{marginBottom:14}}>
                <label style={{fontSize:10,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.5px"}}>Opportunity</label>
                <select className="inp" style={{width:"100%",fontSize:11,marginTop:4,marginBottom:0}}
                  value={q.opportunityId||""}
                  onChange={e=>updateQuote(q.id,{opportunityId:e.target.value||null})}>
                  <option value="">— None —</option>
                  {opts.map(o=>(
                    <option key={o.id} value={o.id}>
                      {o.title||"(untitled)"}{o.companyName?` — ${o.companyName}`:""}{o.closed?" (closed)":""}
                    </option>
                  ))}
                </select>
              </div>
            );
          })()}
  
          {/* Related proposal — optional cross-reference. Undefined
              and null are equivalent, so existing quotes need no migration. */}
          {proposals.length>0&&(
            <div style={{marginBottom:14}}>
              <label style={{fontSize:10,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.5px"}}>Related proposal</label>
              <select className="inp" style={{width:"100%",fontSize:11,marginTop:4,marginBottom:0}}
                value={q.proposalId||""}
                onChange={e=>updateQuote(q.id,{proposalId:e.target.value||null})}>
                <option value="">— None —</option>
                {proposals.filter(p=>p.status!=="archived").map(p=>(
                  <option key={p.id} value={p.id}>
                    {(p.companyName||p.content?.customerName||"Untitled")} — {p.content?.recommendedPlan||"Bronze Sentinel"}
                    {p.snapshots?.length?` (v${p.snapshots.length})`:""}
                  </option>
                ))}
              </select>
            </div>
          )}
  
          {/* Status pills */}
          <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:14}}>
            <span style={{fontSize:10,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.5px",marginRight:4}}>Status:</span>
            {[
              {id:"draft",label:"Drafting",cls:""},
              {id:"sent",label:"Sent",cls:"btn-warn"},
              {id:"won",label:"Won",cls:"btn-O"},
              {id:"lost",label:"Lost",cls:"btn-danger"},
              /* No "Completed" pill: Complete is a COLUMN on the board, derived
                 from the four fulfilment steps — Craig's call. quoteBoard.js. */
            ].map(s=>(
              <button key={s.id} className={`btn ${q.status===s.id?s.cls:""}`} style={{fontSize:10,padding:"4px 12px"}}
                onClick={()=>updateQuote(q.id,{status:s.id, ...(s.id==="sent"&&!q.sentAt?{sentAt:Date.now()}:{}), ...(["won","lost"].includes(s.id)?{decidedAt:Date.now()}:{})})}>
                {s.label}
              </button>
            ))}
          </div>
  
          {/* Description */}
          <div style={{marginBottom:14}}>
            <label style={{fontSize:10,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.5px"}}>Description / Scope</label>
            <textarea className="inp" style={{width:"100%",minHeight:60,resize:"vertical",fontSize:11,marginTop:4,marginBottom:0}}
              value={q.description||""} onChange={e=>updateQuote(q.id,{description:e.target.value})}
              placeholder="What is the client asking for? Paste the email body or describe the scope of work…"/>
          </div>
  
          {/* ── Sections > Options > Line Items, then Common ── */}
          {(()=>{
            const migrated = ensureMigrated(q);
            const sections = migrated.sections || [];
            /* ⛔ DISPLAY ORDER: non-freight sections, then Common Items, then
               freight — exactly what the document prints, or the preview stops
               being a preview. Applied here, never to the stored order, so a
               section added after freight exists still lands above it. */
            const orderedSections = orderSections(sections);
            const mainSections = orderedSections.filter(s => !isFreightSection(s));
            const freightSections = orderedSections.filter(isFreightSection);
            const totals = computeQuoteTotals(migrated);
  
            // Reusable: renders a line items table for a given location.
            // location: "common" or option id.
            const renderLineItemsTable = (location, items) => {
              const isFocused = isSameLocation(focusedLocation, location);
              return (
                <div
                  onClick={()=>setFocusedLocation(location)}
                  style={{
                    border: `1px solid ${isFocused?"var(--strat)":"var(--border)"}`,
                    borderRadius:6,
                    overflow:"hidden",
                    /* NESTED inside a --form-card section: white, so an option
                       reads as a block within its section rather than blending. */
                    background:"var(--s1)",
                    boxShadow: isFocused?"0 0 0 2px rgba(52,152,219,0.15)":"none",
                    transition:"all 0.15s",
                  }}>
                  {/* ⚠ THE HEADER LABELS THE NUMBERS ROW, NOT NINE COLUMNS.
                      It used to carry TYPE / DESCRIPTION / QTY / … across a
                      nine-column grid; once the row stacks, those labels sit
                      above fields that are no longer beneath them, which is
                      worse than cramped — it is misleading.

                      TYPE and DESCRIPTION lost their labels because both are
                      self-describing: a dropdown reading 'Hardware' and a box
                      whose placeholder says 'Description…'. The five numeric
                      labels stayed because UNIT COST and UNIT PRICE are one
                      word apart and the whole margin depends on telling them
                      apart at a glance.

                      It shares .qli-nums with every row, so alignment is by
                      construction rather than by hand. */}
                  <div className="qli-hdr" style={{background:"var(--s3)",borderBottom:"1px solid var(--border)"}}>
                    <div></div>
                    <div className="qli-nums">
                      <div className="qli-lab">Qty</div>
                      <div className="qli-lab">Unit cost</div>
                      <div className="qli-lab">Unit price</div>
                      <div className="qli-lab">Markup</div>
                      <div className="qli-lab">Subtotal</div>
                      <div></div>
                    </div>
                  </div>
                  {items.length===0 ? (
                    <div style={{padding:"18px 12px",textAlign:"center",color:"var(--text3)",fontSize:11,fontStyle:"italic"}}>
                      {isFocused
                        ? <>No line items yet. Click <strong>+ Hardware/Service/Labour/Recurring</strong> below, or use <strong>🤖 AI</strong> at the top of this section.</>
                        : "No line items here."}
                    </div>
                  ) : (
                    items.map(li=>{
                      const _qty   = quantityOf(li);
                      const _price = Number(li.unitPrice)||0;
                      const _cost  = Number(li.unitCost)||0;
                      const subtotal = _qty * _price;
                      const lineMarkup = _cost > 0 ? ((_price - _cost) / _cost) * 100 : 0;
                      const markupColor = lineMarkup >= 20 ? "var(--ops)" : lineMarkup >= 10 ? "var(--warn)" : lineMarkup > 0 ? "var(--danger)" : "var(--text3)";
                      /* One colour per Type. `labour` and `software` arrived 2026-09-20
                         (Craig's accounting categories are Hardware / Software / Labour,
                         and the Autotask ticket prints a labour line's full description);
                         `freight` is what the freight generator now writes instead of the
                         invisible "other". li.type is presentational only — nothing in
                         the totals or the document reads it except periodOf. */
                      const typeColor = ({hardware:"var(--strat)",software:"var(--dt-secondary)",service:"var(--ops)",
                        labour:"var(--warn)",recurring:"var(--fellow)",freight:"var(--text3)",other:"var(--text3)"})[li.type||"hardware"] || "var(--text3)";
                      /* ⚠ The ONLY line that did not survive the move verbatim: a dead
                         `const [supplierOpen, _setSupplierOpen] = [li._supplierOpen, ()=>{}]`
                         destructure. Nothing read either binding — the markup below reads
                         `li._supplierOpen` directly. It passed in MspOsApp.jsx solely
                         because that file opens with a blanket
                         `/* eslint-disable no-unused-vars *​/`, which this file deliberately
                         does not carry: a fresh file should not inherit a suppression that
                         exists to tolerate 16,000 lines of accumulated slack. */
                      return (
                        /* ⚠ ONE ITEM, THREE TIERS. The 3px rail is what says so:
                           the numbers row, the checkbox row and the supplier panel
                           all hang off it, and without it a stacked item reads as
                           several loose strips. It carries the TYPE colour, which
                           the dropdown used to be the only thing showing. */
                        <div key={li.id} className="qli-item" style={{borderLeftColor:typeColor}}>
                          <div className="qli">
                            {/* The thumbnail rail — spans every tier below it. */}
                            <div className="qli-thumb">
                              {li.imageUrl ? (
                                <img src={li.imageUrl} alt="" style={{width:42,height:42,objectFit:"contain",cursor:"pointer",border:"1px solid var(--border)",borderRadius:4,background:"white"}}
                                  onClick={(ev)=>{ev.stopPropagation();setLightboxImage(li.imageUrl);}}
                                  onError={(ev)=>{ev.currentTarget.style.display="none";}}
                                  title="Click to enlarge"/>
                              ) : (
                                <div style={{width:42,height:42,border:"1px dashed var(--border2)",borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,color:"var(--text3)",opacity:0.5}} title="No image">📦</div>
                              )}
                            </div>

                            <div className="qli-main">
                            {/* Tier 1 — what the thing IS. The description gets
                                every pixel the type dropdown does not. */}
                            <div className="qli-top">
                            <select className="inp" style={{marginBottom:0,fontSize:10,padding:"3px 4px",color:typeColor,fontWeight:600,border:"1px solid var(--border)",background:"white"}}
                              value={li.type||"hardware"}
                              onChange={e=>updateLineItem(q.id,location,li.id,{type:e.target.value})}>
                              <option value="hardware">Hardware</option>
                              <option value="software">Software</option>
                              <option value="service">Service</option>
                              <option value="labour">Labour</option>
                              <option value="recurring">Recurring</option>
                              <option value="freight">Freight</option>
                              {/* "other" was the freight generator's value before Freight
                                  existed; older quotes still carry it. Shown only where it
                                  is the stored value, so nothing is invisible. */}
                              {li.type==="other" && <option value="other">Other</option>}
                            </select>
                            <div style={{display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                              <input type="text" className="inp" style={{marginBottom:0,fontSize:11,padding:"3px 6px"}}
                                value={li.description||""} placeholder="Description…"
                                onChange={e=>updateLineItem(q.id,location,li.id,{description:e.target.value})}/>
                              {(li.sku||li.stockNote||li.manufacturer) && (
                                <div style={{fontSize:9,color:"var(--text3)",display:"flex",gap:8,flexWrap:"wrap",paddingLeft:6,lineHeight:1.3}}>
                                  {li.sku && <span style={{color:"var(--strat)",fontWeight:600}}>{li.sku}</span>}
                                  {li.manufacturer && <span>· {li.manufacturer}</span>}
                                  {li.stockNote && <span>· {li.stockNote}</span>}
                                </div>
                              )}
                            </div>
                            </div>{/* /qli-top */}

                            {/* Tier 1b — what the customer should KNOW about it.
                                ⚠ CUSTOMER-FACING. This is li.detail: it prints under the
                                description on the PDF and the web quote, fully expanded.
                                It is NOT li.supplierNotes, which is internal and lives in
                                the supplier panel below — the placeholder says the customer
                                sees it, because that is the one thing that stops internal
                                notes being typed here. Same updateLineItem path as the
                                description above; deliberately absent from
                                FREIGHT_RELEVANT_FIELDS, so typing here never resyncs freight. */}
                            <textarea className="inp qli-detail" rows={10}
                              value={li.detail||""} aria-label="Customer-facing item detail"
                              placeholder="Extra detail for the customer about this item (optional) — appears under the item on the quote."
                              onChange={e=>updateLineItem(q.id,location,li.id,{detail:e.target.value})}/>

                            {/* Tier 2 — what it COSTS. Same grid as the header
                                strip, so the labels above are the labels for
                                these fields and stay that way when one of them
                                is resized. */}
                            <div className="qli-nums">
                            {/* ⚠ A QUANTITY IS NOT MONEY, so this passes prefix="" and
                                decimals={null}. It is the same component as the two
                                money fields below — same no-spinner, same raw string
                                held while focused, same commit-on-blur, so the `010`
                                bug cannot come back here either — but it must not
                                wear a $ and must not settle to "6.00" on blur.
                                Decimals are still ACCEPTED (2.5 boxes of cable);
                                decimals:null only changes how a number is DISPLAYED. */}
                            <MoneyInput value={li.quantity ?? 1} ariaLabel="Quantity"
                              className="inp" prefix="" decimals={null} placeholder="1"
                              inputStyle={{marginBottom:0,fontSize:11,padding:"3px 6px",textAlign:"right"}}
                              onCommit={v=>updateLineItem(q.id,location,li.id,{quantity:v})}/>
                            {/* ⚠ MoneyInput, NOT type="number", and this is a bug fix
                                rather than a restyle. CLAUDE.md records the `010`
                                display bug: a numeric field seeded with a literal 0
                                appends to the zero as you type. Proposals solved it
                                with text + inputMode="decimal", a $ prefix, the raw
                                string held while focused and 2dp on blur — imported
                                here rather than re-derived, so there is one fix and
                                not two.

                                It also commits on BLUR instead of per keystroke,
                                which suits the debounced preview: one render per
                                finished figure rather than one per digit. */}
                            <MoneyInput value={li.unitCost ?? 0} ariaLabel="Unit cost"
                              className="inp"
                              inputStyle={{marginBottom:0,fontSize:11,padding:"3px 6px 3px 16px",textAlign:"right"}}
                              onCommit={v=>updateLineItem(q.id,location,li.id,{unitCost:v})}/>
                            <MoneyInput value={li.unitPrice ?? 0} ariaLabel="Unit price"
                              className="inp"
                              inputStyle={{marginBottom:0,fontSize:11,padding:"3px 6px 3px 16px",textAlign:"right"}}
                              onCommit={v=>updateLineItem(q.id,location,li.id,{unitPrice:v})}/>
                            <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",gap:4}}>
                              <span style={{fontSize:11,fontWeight:600,color:markupColor}}>{_cost>0?`${lineMarkup.toFixed(0)}%`:"—"}</span>
                              {_cost>0 && (
                                <button className="ico-btn" style={{fontSize:10,padding:"0 4px",lineHeight:1,opacity:0.6}}
                                  onClick={()=>updateLineItem(q.id,location,li.id,{unitPrice:Number((_cost*(1+defaultMarkupPct/100)).toFixed(2))})}
                                  title={`Reset to default markup (${defaultMarkupPct}%)`}>↻</button>
                              )}
                            </div>
                            <div style={{textAlign:"right",fontSize:11,fontWeight:600,color:"var(--text)"}}>${subtotal.toFixed(2)}{periodOf(li) ? <span style={{fontSize:9,fontWeight:500,color:"var(--text3)"}}>{periodShort(periodOf(li))}</span> : null}</div>
                            <button className="ico-btn" style={{fontSize:14,padding:"0 4px",color:"var(--danger)"}} onClick={()=>deleteLineItem(q.id,location,li.id)} title="Remove line item">×</button>
                            </div>{/* /qli-nums */}
                            </div>{/* /qli-main */}
                          </div>{/* /qli */}

                          {/* Tier 3 — customer flags + the supplier accordion. */}
                          <div className="qli-sub">
                            <label style={{display:"flex",alignItems:"center",gap:4,cursor:"pointer"}} title="Customer can adjust the quantity on this line">
                              <input type="checkbox" checked={!!li.customerCanEditQty} onChange={e=>updateLineItem(q.id,location,li.id,{customerCanEditQty:e.target.checked})}/>
                              Qty editable
                            </label>
                            <label style={{display:"flex",alignItems:"center",gap:4,cursor:"pointer"}} title="Customer can tick/untick this line to include or exclude it">
                              <input type="checkbox" checked={!!li.customerCanToggle} onChange={e=>updateLineItem(q.id,location,li.id,{customerCanToggle:e.target.checked})}/>
                              Optional (toggleable)
                            </label>
                            <input type="url" className="inp" style={{marginBottom:0,fontSize:10,padding:"2px 6px",flex:"0 1 240px",height:"24px"}}
                              value={li.imageUrl||""}
                              placeholder="Image URL (paste link)"
                              onChange={e=>updateLineItem(q.id,location,li.id,{imageUrl:e.target.value})}/>
                            <button className="btn" style={{fontSize:9,padding:"3px 8px",marginLeft:"auto"}}
                              onClick={()=>updateLineItem(q.id,location,li.id,{_supplierOpen:!li._supplierOpen})}>
                              📦 Supplier info {li._supplierOpen?"▼":"▶"}
                            </button>
                          </div>
                          {/* Supplier accordion */}
                          {li._supplierOpen && (
                            <div style={{padding:"8px 10px 10px 64px",background:"var(--s2)",borderTop:"1px solid var(--border)",display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                              <div>
                                <label style={{fontSize:9,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.5px"}}>Supplier</label>
                                <input type="text" className="inp" style={{width:"100%",marginBottom:0,fontSize:10,padding:"3px 6px",marginTop:2}}
                                  value={li.supplier||""} placeholder="e.g. Leader Computers"
                                  onChange={e=>updateLineItem(q.id,location,li.id,{supplier:e.target.value})}/>
                              </div>
                              <div>
                                <label style={{fontSize:9,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.5px"}}>Supplier SKU / stock code</label>
                                <input type="text" className="inp" style={{width:"100%",marginBottom:0,fontSize:10,padding:"3px 6px",marginTop:2}}
                                  value={li.supplierSku||""} placeholder="Their stock code"
                                  onChange={e=>updateLineItem(q.id,location,li.id,{supplierSku:e.target.value})}/>
                              </div>
                              <div>
                                <label style={{fontSize:9,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.5px"}}>Product URL (supplier page)</label>
                                <input type="url" className="inp" style={{width:"100%",marginBottom:0,fontSize:10,padding:"3px 6px",marginTop:2}}
                                  value={li.supplierUrl||""} placeholder="https://…"
                                  onChange={e=>updateLineItem(q.id,location,li.id,{supplierUrl:e.target.value})}/>
                              </div>
                              <div>
                                <label style={{fontSize:9,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.5px"}}>Notes (lead time, MOQ, etc)</label>
                                <input type="text" className="inp" style={{width:"100%",marginBottom:0,fontSize:10,padding:"3px 6px",marginTop:2}}
                                  value={li.supplierNotes||""} placeholder="e.g. 10-day lead, MOQ 5"
                                  onChange={e=>updateLineItem(q.id,location,li.id,{supplierNotes:e.target.value})}/>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              );
            };
  
            // Action bar (Add hardware/service/recurring etc) — operates on a given location
            const renderActionBar = (location) => {
              const isFocused = isSameLocation(focusedLocation, location);
              return (
                <div style={{display:"flex",gap:6,alignItems:"center",marginTop:8,flexWrap:"wrap"}}>
                  <button className="btn" style={{fontSize:10,padding:"4px 9px"}} onClick={()=>{setFocusedLocation(location);addLineItem(q.id,location,"hardware");}}>+ Hardware</button>
                  <button className="btn" style={{fontSize:10,padding:"4px 9px"}} onClick={()=>{setFocusedLocation(location);addLineItem(q.id,location,"service");}}>+ Service</button>
                  <button className="btn" style={{fontSize:10,padding:"4px 9px"}} onClick={()=>{setFocusedLocation(location);addLineItem(q.id,location,"labour");}}>+ Labour</button>
                  <button className="btn" style={{fontSize:10,padding:"4px 9px"}} onClick={()=>{setFocusedLocation(location);addLineItem(q.id,location,"recurring");}}>+ Recurring</button>
                  {!isFocused && (
                    <span style={{fontSize:9,color:"var(--text3)",fontStyle:"italic",marginLeft:6}}>(click this section to focus AI/templates here)</span>
                  )}
                  {isFocused && (
                    <span style={{fontSize:9,color:"var(--strat)",fontWeight:600,marginLeft:6}}>● AI/templates target this section</span>
                  )}
                </div>
              );
            };
  
            // One section card: header (drag / label / reorder / delete)
            // then ONE CARD PER OPTION, each with its own radio, description,
            // line items and add buttons.
            //
            // ⚠ THERE IS NO "SHOWN" OPTION ANY MORE. The old layout put every
            // option in a chip row and a single editor beneath showing whichever
            // chip was last CLICKED — so a chip could be dark (its editor is
            // open) while a different chip was green (it is what the customer
            // gets), and the two meant unrelated things. Craig: "have the
            // details of each item under the option radio that relates to it…
            // all detail for each option displayed, with the default selected
            // option remembered based on what I select it to be."
            //
            // The radio is unchanged: it sets section.selectedOptionId, which
            // persists on the record and is what the document counts. Only
            // where it sits has moved.
            const renderSection = (section, sIdx) => {
              const sTotals = totals.sectionTotals.find(st => st.id===section.id);
              const isSectionFocused = focusedLocation?.sectionId === section.id;
              const options = section.options || [];
              /* The generated freight section: pinned last, not draggable, not
                 movable. sIdx is its DISPLAY index (after every main section),
                 so its § number is the last one — matching the document. */
              const freight = isFreightSection(section);
              // Clicking the section itself (its label) focuses the option that
              // goes out — or the first — so AI/templates land somewhere real.
              const defaultOpt = options.find(o => o.id === section.selectedOptionId) || options[0] || null;
              return (
                <div key={section.id}
                  onDragOver={e=>e.preventDefault()}
                  onDrop={e=>{
                    e.preventDefault();
                    const from = dragSectionIndexRef.current;
                    dragSectionIndexRef.current = null;
                    /* Indices are over the NON-FREIGHT list. A section dropped
                       past freight lands ABOVE it — at the end of that list —
                       never below. */
                    const to = freight ? mainSections.length - 1 : sIdx;
                    if(from==null || from===to) return;
                    reorderSections(q.id, from, to);
                  }}
                  style={{
                    marginBottom:12,
                    border:`1px solid ${isSectionFocused?"var(--strat)":"var(--border)"}`,
                    borderLeft:`4px solid ${isSectionFocused?"var(--strat)":"var(--border2)"}`,
                    /* THE section card: the shared --form-card fill, a step darker
                       than the white form so each section is its own block. */
                    borderRadius:6, background:"var(--form-card)", overflow:"hidden",
                    boxShadow: isSectionFocused?"0 2px 8px rgba(52,152,219,0.15)":"none",
                    transition:"all 0.15s",
                  }}>
                  {/* Section header */}
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:isSectionFocused?"var(--strat-d)":"var(--s3)",borderBottom:"1px solid var(--border)"}}>
                    {freight ? (
                      /* No drag handle: a pinned section that could still be
                         dragged would snap back and read as a bug. */
                      <span title="Freight is always the last section — below the common items — so it cannot be dragged"
                        style={{fontSize:12,color:"var(--text3)",userSelect:"none",padding:"0 2px",flexShrink:0}}>📌</span>
                    ) : (
                    <div draggable
                      onDragStart={e=>{
                        dragSectionIndexRef.current = sIdx;
                        e.dataTransfer.effectAllowed="move";
                        e.dataTransfer.setData("text/plain", section.id);
                      }}
                      onDragEnd={()=>{dragSectionIndexRef.current=null;}}
                      title="Drag to reorder"
                      style={{cursor:"grab",fontSize:13,color:"var(--text3)",userSelect:"none",padding:"0 2px",flexShrink:0}}>⋮⋮</div>
                    )}
                    <span style={{fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.5px",flexShrink:0}}>§{sIdx+1}</span>
                    <input type="text" className="inp" style={{flex:1,marginBottom:0,fontSize:12,fontWeight:600,padding:"4px 8px"}}
                      value={section.label||""}
                      placeholder="e.g. Turret Cameras"
                      onClick={()=>setFocusedLocation({sectionId:section.id,optionId:defaultOpt?.id||null})}
                      onChange={e=>updateSection(q.id,section.id,{label:e.target.value})}/>
                    {section.createdByAI && (
                      <span title="This section was created by the AI assistant"
                        style={{fontSize:9,color:"var(--strat)",background:"var(--strat-d)",padding:"2px 6px",borderRadius:8,fontWeight:700,letterSpacing:"0.5px",flexShrink:0}}>AI</span>
                    )}
                    <div style={{fontSize:11,fontWeight:700,color:"var(--text)",padding:"4px 10px",background:"white",border:"1px solid var(--border)",borderRadius:6,whiteSpace:"nowrap"}}>
                      {streamsText({subtotal:sTotals?.sectionSubtotal||0,onceOff:sTotals?.sectionOnceOff,recurring:sTotals?.sectionRecurring})}
                    </div>
                    {freight ? (
                      <span data-testid="freight-pinned" title="Freight stays at the bottom of the quote, below the common items, in the builder, on the web view and in the PDF"
                        style={{fontSize:9,fontWeight:700,color:"var(--text3)",background:"var(--s2)",border:"1px solid var(--border)",padding:"2px 7px",borderRadius:8,letterSpacing:"0.4px",whiteSpace:"nowrap",flexShrink:0}}>
                        always last
                      </span>
                    ) : (
                    <div style={{display:"flex",gap:2,flexShrink:0}}>
                      <button className="ico-btn" onClick={()=>moveSection(q.id,section.id,-1)} disabled={sIdx===0} title="Move section up">↑</button>
                      <button className="ico-btn" onClick={()=>moveSection(q.id,section.id,+1)} disabled={sIdx===mainSections.length-1} title="Move section down">↓</button>
                    </div>
                    )}
                    <button className="ico-btn" style={{color:"var(--danger)",flexShrink:0}}
                      onClick={()=>confirmAndDeleteSection(q.id,section.id)} title="Delete this section">×</button>
                  </div>
  
                  <div style={{padding:12}}>
                    <textarea className="inp" style={{width:"100%",fontSize:11,marginBottom:10,marginTop:0,padding:"6px 8px",minHeight:32,resize:"vertical",fontFamily:"inherit",lineHeight:1.4}}
                      value={section.description||""}
                      placeholder="Customer-facing description for this section (optional)"
                      onChange={e=>updateSection(q.id,section.id,{description:e.target.value})}/>
  
                    {/* ── OPTION CARDS ──────────────────────────────────────
                        One per option, stacked, every one fully expanded. The
                        SELECTED card is the one that goes out with the quote,
                        and with every card now carrying equal detail a radio
                        dot alone cannot carry that — so it is tinted, edged,
                        and labelled in the document's own words ("Included in
                        the total") so the builder and the PDF agree.

                        Two meanings, two signals, deliberately kept apart:
                          green edge + words  = what the customer gets
                                                (section.selectedOptionId)
                          blue ring on lines  = where AI / templates / + buttons
                                                land (focusedLocation)
                        The + Hardware / + Service / + Recurring buttons inside
                        a card add to THAT card — addLineItem takes an explicit
                        location — and focus follows the card last touched.

                        No collapse toggle: hiding detail is the problem this
                        layout removes. An option with no lines yet is short by
                        itself. */}
                    {options.length === 0 ? (
                      <div style={{padding:"16px 12px",textAlign:"center",color:"var(--text3)",fontSize:11,border:"1px dashed var(--border2)",borderRadius:6}}>
                        No options yet.
                        <button className="btn btn-strat" style={{fontSize:10,padding:"3px 9px",marginLeft:8}}
                          onClick={()=>addOption(q.id,section.id)}>+ Add first option</button>
                      </div>
                    ) : (
                      <>
                        {options.map((opt, oIdx) => {
                          const multi   = options.length > 1;
                          const loc     = {sectionId:section.id, optionId:opt.id};
                          const isSel   = section.selectedOptionId === opt.id;
                          // What the document COUNTS: the explicit pick, else the
                          // first option as a preview — the renderer's own rule.
                          const counted = sTotals?.previewOptionId === opt.id;
                          const oT      = sTotals?.optionTotals.find(ot=>ot.id===opt.id);
                          return (
                            <div key={opt.id} data-testid={`option-card-${opt.id}`}
                              style={{
                                border:`1px solid ${multi&&counted?"var(--ops)":"var(--border)"}`,
                                borderLeft:`4px solid ${multi&&counted?"var(--ops)":"var(--border2)"}`,
                                borderRadius:6, padding:10, marginBottom: oIdx===options.length-1?0:8,
                                background: multi&&counted?"var(--ops-d)":"var(--s2)",
                              }}>
                              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                                {multi ? (
                                  <input type="radio" name={`sel-${section.id}`} checked={isSel}
                                    onChange={()=>{selectOption(q.id,section.id,opt.id);setFocusedLocation(loc);}}
                                    title="Mark as the option the customer gets"
                                    aria-label={`Send ${opt.label||"this option"} to the customer`}
                                    style={{cursor:"pointer",margin:0,flexShrink:0,width:15,height:15,accentColor:"var(--ops)"}}/>
                                ) : (
                                  <span style={{fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.5px",flexShrink:0}}>Option</span>
                                )}
                                <input type="text" className="inp" style={{flex:1,marginBottom:0,fontSize:11,fontWeight:600,padding:"3px 8px"}}
                                  value={opt.label||""}
                                  placeholder="Option name, e.g. G4 Pro"
                                  onClick={()=>setFocusedLocation(loc)}
                                  onChange={e=>updateOption(q.id,section.id,opt.id,{label:e.target.value})}/>
                                {multi && (
                                  counted ? (
                                    <span style={{fontSize:9,fontWeight:700,color:"white",background:"var(--ops)",padding:"2px 7px",borderRadius:8,letterSpacing:"0.4px",whiteSpace:"nowrap",flexShrink:0}}
                                      title={isSel ? "This option is counted in the total and goes out with the quote" : "No option is ticked yet, so the document counts the first one — tick a radio to choose"}>
                                      Included in the total{isSel ? "" : " (preview)"}
                                    </span>
                                  ) : (
                                    <span style={{fontSize:9,fontWeight:600,color:"var(--text3)",background:"white",border:"1px solid var(--border)",padding:"2px 7px",borderRadius:8,letterSpacing:"0.4px",whiteSpace:"nowrap",flexShrink:0}}>
                                      Alternative
                                    </span>
                                  )
                                )}
                                <span style={{fontSize:11,fontWeight:700,color:"var(--text)",whiteSpace:"nowrap",flexShrink:0}}>{streamsText(oT)}</span>
                                <button className="ico-btn" style={{color:"var(--danger)",flexShrink:0}}
                                  onClick={()=>confirmAndDeleteOption(q.id,section.id,opt.id)} title="Delete this option">×</button>
                              </div>
                              <textarea className="inp" style={{width:"100%",fontSize:11,marginBottom:8,marginTop:0,padding:"6px 8px",minHeight:32,resize:"vertical",fontFamily:"inherit",lineHeight:1.4,background:"white"}}
                                value={opt.description||""}
                                placeholder={`Customer-facing description for ${opt.label||"this option"} (optional)`}
                                onClick={()=>setFocusedLocation(loc)}
                                onChange={e=>updateOption(q.id,section.id,opt.id,{description:e.target.value})}/>
                              {renderLineItemsTable(loc, opt.lineItems||[])}
                              {renderActionBar(loc)}
                            </div>
                          );
                        })}
                        <div style={{marginTop:8}}>
                          <button className="btn" style={{fontSize:10,padding:"4px 9px"}}
                            onClick={()=>addOption(q.id,section.id)} title="Add another option to this section">+ Option</button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            };
  
            return (
              <>
                {/* Top-level toolbar: AI + templates + add section */}
                <div style={{marginBottom:10,padding:"10px 12px",background:"var(--s2)",borderRadius:6,border:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
                  <div style={{display:"flex",flexDirection:"column",gap:2}}>
                    <label style={{fontSize:10,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.5px"}}>Quote structure</label>
                    <div style={{fontSize:10,color:"var(--text2)"}}>
                      {sections.length} section{sections.length===1?"":"s"} · {sumOptions(sections)} option{sumOptions(sections)===1?"":"s"} · {(migrated.commonLineItems||[]).length} common item{(migrated.commonLineItems||[]).length===1?"":"s"} · Customer picks one option per section
                    </div>
                  </div>
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    {quoteTemplates.length>0 && (
                      <select className="inp" style={{fontSize:10,padding:"3px 8px",marginBottom:0,width:140}}
                        value=""
                        onChange={e=>{if(e.target.value){applyTemplate(q.id,e.target.value);e.target.value="";}}}>
                        <option value="">Apply template…</option>
                        {quoteTemplates.map(t=>(
                          <option key={t.id} value={t.id}>
                            {t.name} ({templateItemCount(t)} items{isSectionTemplate(t)?", section":""})
                          </option>
                        ))}
                      </select>
                    )}
                    <button className="btn btn-strat" style={{fontSize:10,padding:"4px 9px"}} onClick={()=>openAiChat(q)} title="Open AI chat assistant — describe what you need and refine across multiple turns">🤖 AI assist</button>
                    <button className="btn" style={{fontSize:10,padding:"4px 9px"}} onClick={()=>addSection(q.id)} title="Add a section (a group the customer picks one option from)">+ Section</button>
                  </div>
                </div>
  
                {/* Sections */}
                {sections.length === 0 ? (
                  <div style={{padding:"32px 20px",textAlign:"center",border:"1px dashed var(--border2)",borderRadius:6,marginBottom:14,color:"var(--text3)"}}>
                    <div style={{fontSize:34,opacity:0.4,marginBottom:8}}>📋</div>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--text2)",marginBottom:4}}>No sections yet</div>
                    <div style={{fontSize:11,lineHeight:1.6}}>
                      Sections group related choices (e.g. “Turret Cameras”, “Bullet Cameras”).<br/>
                      Click <strong>+ Section</strong> above to start manually, or <strong>🤖 AI assist</strong> to have AI structure it from a description.
                    </div>
                  </div>
                ) : (
                  <>
                    {mainSections.map((s, i) => renderSection(s, i))}
                    <button className="btn" style={{fontSize:10,padding:"5px 9px",width:"100%",marginBottom:14,borderStyle:"dashed"}}
                      onClick={()=>addSection(q.id)}>+ Section</button>
                  </>
                )}
  
                {/* Common items section */}
                <div style={{marginBottom:14,padding:"10px 12px",border:`1px solid ${focusedLocation==="common"?"var(--strat)":"var(--border)"}`,borderLeft:`4px solid ${focusedLocation==="common"?"var(--strat)":"var(--border2)"}`,borderRadius:6,background:"white"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                    <span style={{fontSize:10,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.5px"}}>Common items</span>
                    <span style={{fontSize:10,color:"var(--text3)",fontStyle:"italic"}}>Always included, whichever options are chosen</span>
                    <div style={{flex:1}}/>
                    <div style={{fontSize:11,fontWeight:700,color:"var(--text)",padding:"4px 10px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:6,whiteSpace:"nowrap"}}>
                      {streamsText({subtotal:totals.commonSubtotal,onceOff:totals.commonOnceOff,recurring:totals.commonRecurring})}
                    </div>
                  </div>
                  {renderLineItemsTable("common", migrated.commonLineItems||[])}
                  {renderActionBar("common")}
                </div>

                {/* ⛔ FREIGHT, AFTER THE COMMON ITEMS — the document's order.
                    Numbered after every main section so its § matches the page. */}
                {freightSections.map((s, k) => renderSection(s, mainSections.length + k))}
              </>
            );
          })()}
  
          {/* Totals & margin panel */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 320px",gap:14,marginBottom:14}}>
            <div>
              <label style={{fontSize:10,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.5px"}}>Internal notes (not sent to client)</label>
              <textarea className="inp" style={{width:"100%",minHeight:60,resize:"vertical",fontSize:11,marginTop:4,marginBottom:0}}
                value={q.notes||""} onChange={e=>updateQuote(q.id,{notes:e.target.value})}
                placeholder="Margin assumptions, supplier notes, follow-up reminders…"/>
            </div>
            {/* Freight — only once the quote has something to ship. */}
            {countAllLineItems(q) > 0 && (()=>{
              const fr  = ensureMigrated(q).freight || {};
              const bd  = fr.lastComputedBreakdown;
              const manual = fr.mode === "manual";
              return (
                <div style={{background:"var(--form-card)",border:"1px solid var(--border2)",borderRadius:6,padding:12,marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <div style={{fontSize:10,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.5px"}}>
                      Freight {manual ? "— manual" : "— auto"}
                    </div>
                    <button className="btn" style={{fontSize:10}}
                      onClick={()=> manual ? switchToManagedFreight(q.id) : switchToManualFreight(q.id)}>
                      {manual ? "↺ Auto-calculate" : "✎ Enter manually"}
                    </button>
                  </div>
  
                  {manual ? (
                    <>
                      <input type="number" className="inp" style={{width:"100%",fontSize:11,marginBottom:4}}
                        placeholder="Freight amount ex GST"
                        value={fr.manualOverride?.totalAmount ?? ""}
                        onChange={e=>setManualFreight(q.id, e.target.value, fr.manualOverride?.note)}/>
                      <input type="text" className="inp" style={{width:"100%",fontSize:11}}
                        placeholder="Note (e.g. 'Customer collect from Malaga')"
                        value={fr.manualOverride?.note || ""}
                        onChange={e=>setManualFreight(q.id, fr.manualOverride?.totalAmount, e.target.value)}/>
                    </>
                  ) : !bd ? (
                    <div style={{fontSize:10,color:"var(--text3)",fontStyle:"italic"}}>
                      Not calculated yet — add items with a supplier, or edit one, to trigger a recalculation.
                    </div>
                  ) : (
                    <>
                      {(bd.perSupplier||[]).length === 0 ? (
                        <div style={{fontSize:10,color:"var(--text3)",fontStyle:"italic"}}>
                          No line items are attributed to a configured supplier, so no freight applies.
                        </div>
                      ) : (bd.perSupplier||[]).map(p=>(
                        <div key={p.supplierId} style={{fontSize:10,color:"var(--text3)",marginBottom:3}}>
                          <span style={{fontWeight:600,color:"var(--text2)"}}>{p.supplierName}: ${(p.subtotal||0).toFixed(2)}</span>
                          <br/>{p.breakdown}
                        </div>
                      ))}
                      {(bd.warnings||[]).length > 0 && (
                        <div style={{fontSize:10,color:"var(--warn)",marginTop:6}}>
                          ⚠ {bd.warnings.map(w=>w.message).join(" · ")}
                        </div>
                      )}
                      {(bd.itemsPendingWeight||[]).length > 0 && (
                        <div style={{fontSize:9,color:"var(--text3)",marginTop:4,fontStyle:"italic"}}>
                          {bd.itemsPendingWeight.length} item{bd.itemsPendingWeight.length===1?"":"s"} used an estimated weight.
                        </div>
                      )}
                    </>
                  )}
  
                  <label style={{fontSize:10,display:"flex",alignItems:"center",gap:5,marginTop:8,color:"var(--text3)"}}>
                    <input type="checkbox"
                      checked={fr.visibleToCustomer !== false}
                      onChange={e=>setFreightVisibility(q.id, e.target.checked)}/>
                    Show freight to the customer
                  </label>
                </div>
              );
            })()}
  
            <div style={{background:"var(--form-card)",border:"1px solid var(--border2)",borderRadius:6,padding:12}}>
              <div style={{fontSize:10,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:8}}>Totals (preview)</div>
              {/* Per-section rollup: every option listed, the one
                  that counts toward the total marked. */}
              {totals.sectionTotals.length > 0 && (
                <div style={{marginBottom:8,paddingBottom:8,borderBottom:"1px solid var(--border)"}}>
                  {totals.sectionTotals.map(st => (
                    <div key={st.id} style={{marginBottom:6}}>
                      <div style={{fontSize:10,fontWeight:700,color:"var(--text2)",marginBottom:2}}>{st.label}</div>
                      {st.optionTotals.length === 0 ? (
                        <div style={{fontSize:9,color:"var(--text3)",fontStyle:"italic",paddingLeft:8}}>no options</div>
                      ) : st.optionTotals.map(ot => {
                        const counts = ot.id === st.previewOptionId;
                        return (
                          <div key={ot.id} style={{display:"flex",justifyContent:"space-between",fontSize:10,marginBottom:1,paddingLeft:8,color:counts?"var(--text)":"var(--text3)"}}>
                            <span>{ot.label}{counts && !st.selectedOptionId ? " (preview)" : ""}</span>
                            <span style={{fontWeight:counts?700:500}}>{streamsText(ot)}{counts?" ←":""}</span>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  {totals.commonSubtotal > 0 && (
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:"var(--text2)",fontStyle:"italic",marginTop:4,paddingTop:4,borderTop:"1px dashed var(--border)"}}>
                      <span>+ Common (always)</span>
                      <span style={{fontWeight:600}}>{streamsText({subtotal:totals.commonSubtotal,onceOff:totals.commonOnceOff,recurring:totals.commonRecurring})}</span>
                    </div>
                  )}
                </div>
              )}
              {/* ⛔ ONE BLOCK, OR TWO — NEVER A SUM OF THE TWO. With no Recurring
                  line this is the panel it always was. With one, the combined
                  `total` is not shown anywhere: a once-off block and a monthly
                  block, each with its own cost, price, profit and margin — the
                  same split the customer document prints, so Craig's figure and
                  the client's document cannot disagree. */}
              {(() => {
                const pickLabel = totals.sectionTotals.length>0 ? (totals.allSectionsSelected?" (selected)":" (preview)") : "";
                const streamRows = (st, suffix, key) => (
                  <React.Fragment key={key}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:4}}>
                      <span style={{color:"var(--text2)"}}>Total cost</span>
                      <span style={{color:"var(--text)",fontWeight:600}}>${st.totalCost.toFixed(2)}{suffix}</span>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:4}}>
                      <span style={{color:"var(--text2)"}}>Total price{pickLabel}</span>
                      <span style={{color:"var(--text)",fontWeight:600}}>${st.subtotal.toFixed(2)}{suffix}</span>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:4,paddingTop:6,borderTop:"1px solid var(--border)"}}>
                      <span style={{color:"var(--ops)",fontWeight:600}}>Profit</span>
                      <span style={{color:"var(--ops)",fontWeight:700}}>${st.profit.toFixed(2)}{suffix}</span>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:"var(--text3)",marginTop:4}}>
                      <span>Margin</span>
                      <span style={{fontWeight:600,color:st.marginPct>=20?"var(--ops)":st.marginPct>=10?"var(--warn)":"var(--danger)"}}>{st.marginPct.toFixed(1)}%</span>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:"var(--text3)"}}>
                      <span>Markup</span>
                      <span style={{fontWeight:600}}>{st.markupPct.toFixed(1)}%</span>
                    </div>
                  </React.Fragment>
                );
                if (!totals.hasRecurring) return streamRows(totals, "", "all");
                const heading = (text, first) => (
                  <div key={"h-"+text} style={{fontSize:9,fontWeight:700,color:"var(--strat)",textTransform:"uppercase",letterSpacing:"0.6px",marginTop:first?0:10,marginBottom:4,paddingBottom:2,borderBottom:"1px solid var(--border)"}}>{text}</div>
                );
                return [
                  ...(totals.hasOnceOff ? [heading("Once-off", true), streamRows(totals.onceOff, "", "once")] : []),
                  heading(periodHeading(totals.recurring.period), !totals.hasOnceOff),
                  streamRows(totals.recurring, periodShort(totals.recurring.period), "rec"),
                ];
              })()}
            </div>
          </div>

          {/* ── TERMS & CONDITIONS ─────────────────────────────────────────
              Last in the left column because that is where it prints: after
              the items and the totals, at the foot of the document.

              ⚠ These clauses are THIS QUOTE'S OWN COPY, snapshotted from
              Settings when it was created. Editing the Settings default does
              not reach back into an existing quote, and that is deliberate —
              quoteTerms.js explains why. The note below says so on screen,
              because otherwise it reads as a bug the first time Craig edits
              the default and an open draft does not move. */}
          <div style={{marginTop:12,border:"1px solid var(--border)",borderRadius:10,background:"white"}}>
            <div style={{padding:"9px 12px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:12,fontWeight:700,color:"var(--text)"}}>Terms &amp; conditions</span>
              <span style={{fontSize:10,color:"var(--text3)"}}>prints after the totals</span>
              <div style={{flex:1}}/>
              <button className="btn" style={{fontSize:10,padding:"3px 8px"}}
                onClick={()=>addQuoteClause(q.id)}>+ Clause</button>
            </div>
            <div style={{padding:12,display:"flex",flexDirection:"column",gap:10}}>
              {(q.terms||[]).map((c, i) => (
                <div key={c.id} style={{border:"1px solid var(--border)",borderRadius:8,overflow:"hidden"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,padding:"5px 8px",background:"var(--s2)",borderBottom:"1px solid var(--border)"}}>
                    <span style={{fontSize:10,fontWeight:700,color:"var(--text2)"}}>{i+1}.</span>
                    {c.required && (
                      <span style={{fontSize:9,fontWeight:600,letterSpacing:"0.5px",textTransform:"uppercase",color:"var(--strat)",background:"rgba(11,128,165,0.10)",border:"1px solid rgba(11,128,165,0.28)",borderRadius:10,padding:"1px 7px"}}>Always included</span>
                    )}
                    <div style={{flex:1}}/>
                    {c.required ? (
                      /* ⚠ EDITABLE, but not deletable. A client occasionally
                         negotiates a term and the quote has to say what was
                         actually agreed — so the way back to the standard
                         wording is a button, not a re-creation of the quote. */
                      <button className="ico-btn" title="Restore the standard wording from Settings"
                        style={{fontSize:10,padding:"0 6px"}}
                        onClick={()=>restoreQuoteClause(q.id)}>↻ Restore default</button>
                    ) : (
                      <>
                        <button className="ico-btn" title="Move up" style={{fontSize:11,padding:"0 5px"}}
                          onClick={()=>moveQuoteClause(q.id,c.id,-1)}>↑</button>
                        <button className="ico-btn" title="Move down" style={{fontSize:11,padding:"0 5px"}}
                          onClick={()=>moveQuoteClause(q.id,c.id,1)}>↓</button>
                        <button className="ico-btn" title="Delete clause" style={{fontSize:11,padding:"0 5px",color:"var(--danger)"}}
                          onClick={()=>removeQuoteClause(q.id,c.id)}>🗑</button>
                      </>
                    )}
                  </div>
                  <textarea className="inp" rows={c.required?6:3}
                    style={{marginBottom:0,fontSize:11,lineHeight:1.5,border:"none",borderRadius:0,resize:"vertical"}}
                    placeholder="Clause text — plain sentences. Leave a blank line between paragraphs."
                    value={c.text||""}
                    onChange={e=>updateQuoteClause(q.id,c.id,e.target.value)}/>
                </div>
              ))}
              {(q.terms||[]).length===0 && (
                <div style={{fontSize:11,color:"var(--text3)",fontStyle:"italic"}}>
                  No terms on this quote yet — they are added when it is next saved.
                </div>
              )}
              <div style={{fontSize:10,color:"var(--text3)",lineHeight:1.5,borderTop:"1px dashed var(--border)",paddingTop:8}}>
                This quote keeps its own copy of the terms, taken from Settings when it was
                created. Editing the default in Settings changes new quotes only — what a
                customer was sent stays what they were sent.
              </div>
            </div>
          </div>

        </div>

        {/* ── RIGHT: the document itself, rendered by the server. ───────────── */}
        <QuoteDocumentPreview quote={q} ensureMigrated={ensureMigrated} />
      </div>
    </div>
  );
}
